import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Download, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SummaryCards } from '../components/SummaryCards';
import { WorkstreamLegend } from '../components/WorkstreamLegend';
import { ProjectTable } from '../components/ProjectTable';
import { api } from '../lib/api';

export default function PublicBoard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublic = async () => {
      try {
        const result = await api.get(`/api/public/${token}`);
        setData(result);
      } catch (e) {
        setError('This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchPublic();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-4">
        <Shield className="h-12 w-12 text-muted-foreground/40" />
        <h1 className="font-heading text-xl font-semibold">Link Not Found</h1>
        <p className="text-sm text-muted-foreground">{error || 'This link may have expired or been revoked.'}</p>
      </div>
    );
  }

  const handleExport = (format) => {
    // Public users can't export via API (no auth), but can print
    window.print();
  };

  return (
    <div className="min-h-screen bg-background print-full-width">
      {/* Read-only banner */}
      <div className="bg-[hsl(var(--rag-blue-bg))] border-b px-6 py-2 no-print" data-testid="public-board-readonly-banner">
        <div className="flex items-center justify-center gap-2 text-sm text-[hsl(var(--rag-blue))]">
          <Shield className="h-3.5 w-3.5" />
          Read-only shared view
        </div>
      </div>

      {/* Header */}
      <div className="border-b bg-card px-6 py-4 no-print">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">{data.board.name}</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Last updated {new Date(data.board.last_updated).toLocaleDateString()}
            </p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.print()} data-testid="public-board-print-button">
            <Printer className="h-3.5 w-3.5" />
            Print
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6 max-w-7xl mx-auto">
        <WorkstreamLegend workstreams={data.workstreams} />
        <SummaryCards summary={data.summary} />
        <ProjectTable
          projects={data.projects}
          workstreams={data.workstreams}
          readOnly={true}
        />
      </div>

      {/* Footer */}
      <div className="border-t px-6 py-4 text-center text-xs text-muted-foreground no-print">
        Powered by Project RAG Tracker
      </div>
    </div>
  );
}
